import { useQuery } from '@tanstack/react-query';

import axiosInstance from '../lib/axios';
import type { DashboardEmailIntegrationMonitoring, DashboardOverview } from '../types/admin';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const dashboardKeys = {
  all: ['admin', 'dashboard'] as const,
  overview: (params?: DashboardOverviewParams) => [...dashboardKeys.all, 'overview', params] as const,
  emailIntegration: (params?: DashboardEmailMonitoringParams) =>
    [...dashboardKeys.all, 'email-integration', params] as const,
};

export interface DashboardOverviewParams {
  includeAnonymous?: boolean;
}

export interface DashboardEmailMonitoringParams {
  windowHours?: number;
  limit?: number;
}

const fetchDashboardOverview = async (
  params?: DashboardOverviewParams
): Promise<DashboardOverview> => {
  const response = await axiosInstance.get<ApiResponse<DashboardOverview>>(
    '/v1/admin/dashboard/overview',
    {
      params: {
        includeAnonymous: params?.includeAnonymous || undefined,
      },
    }
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Dashboard overview response is empty');
  }

  return response.data.data;
};

const fetchEmailIntegrationMonitoring = async (
  params?: DashboardEmailMonitoringParams
): Promise<DashboardEmailIntegrationMonitoring> => {
  const response = await axiosInstance.get<ApiResponse<DashboardEmailIntegrationMonitoring>>(
    '/v1/admin/dashboard/email-integration',
    {
      params: {
        windowHours: params?.windowHours,
        limit: params?.limit,
      },
    }
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Email integration monitoring response is empty');
  }

  return response.data.data;
};

export const useAdminDashboardOverview = (params?: DashboardOverviewParams) => {
  return useQuery({
    queryKey: dashboardKeys.overview(params),
    queryFn: () => fetchDashboardOverview(params),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
};

export const useEmailIntegrationMonitoring = (params?: DashboardEmailMonitoringParams) => {
  return useQuery({
    queryKey: dashboardKeys.emailIntegration(params),
    queryFn: () => fetchEmailIntegrationMonitoring(params),
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });
};
